import React, { useState, useEffect } from 'react';
import './AllModal.css';
import './ConvertToItineraryModal.css';

function ConvertToItineraryModal({ proposal, onClose, onConvert }) {
  const [activeTab, setActiveTab] = useState('hotels');
  const [selectedItems, setSelectedItems] = useState({
    hotels: [],
    flights: [],
    recommendations: []
  });

  // Initialize all accepted items as selected
  useEffect(() => {
    if (proposal) {
      // Get accepted hotels
      const acceptedHotels = proposal.hotels
        ?.map((hotel, index) => ({ ...hotel, originalIndex: index }))
        .filter(hotel => hotel.status === 'accepted') || [];

      // Get accepted flights
      const acceptedFlights = proposal.flights
        ?.map((flight, index) => ({ ...flight, originalIndex: index }))
        .filter(flight => flight.status === 'accepted') || [];

      // Get interested recommendations
      const interestedRecs = proposal.recommendations
        ?.map((rec, index) => ({ ...rec, originalIndex: index }))
        .filter(rec => rec.isInterested) || [];

      // Pre-select all items
      setSelectedItems({
        hotels: acceptedHotels.map(h => h.originalIndex),
        flights: acceptedFlights.map(f => f.originalIndex),
        recommendations: interestedRecs.map(r => r.originalIndex)
      });
    }
  }, [proposal]);

  const handleCheckboxChange = (category, index) => {
    setSelectedItems(prev => ({
      ...prev,
      [category]: prev[category].includes(index)
        ? prev[category].filter(i => i !== index)
        : [...prev[category], index]
    }));
  };

  const handleConvert = () => {
    onConvert(selectedItems);
  };

  const getAcceptedHotels = () => {
    return proposal.hotels
      ?.map((hotel, index) => ({ ...hotel, originalIndex: index }))
      .filter(hotel => hotel.status === 'accepted') || [];
  };

  const getAcceptedFlights = () => {
    return proposal.flights
      ?.map((flight, index) => ({ ...flight, originalIndex: index }))
      .filter(flight => flight.status === 'accepted') || [];
  };

  const getInterestedRecommendations = () => {
    return proposal.recommendations
      ?.map((rec, index) => ({ ...rec, originalIndex: index }))
      .filter(rec => rec.isInterested) || [];
  };

  const acceptedHotels = getAcceptedHotels();
  const acceptedFlights = getAcceptedFlights();
  const interestedRecs = getInterestedRecommendations();

  const totalItems = acceptedHotels.length + acceptedFlights.length + interestedRecs.length;
  const selectedCount = selectedItems.hotels.length + selectedItems.flights.length + selectedItems.recommendations.length;

  return (
    <div className="modal-overlay">
      <div className="convert-itinerary-modal">
        <div className="modal-header">
          <h2>Convert to Itinerary</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="convert-tabs">
          <button
            className={`convert-tab ${activeTab === 'hotels' ? 'active' : ''}`}
            onClick={() => setActiveTab('hotels')}
          >
            Hotels ({acceptedHotels.length})
          </button>
          <button
            className={`convert-tab ${activeTab === 'flights' ? 'active' : ''}`}
            onClick={() => setActiveTab('flights')}
          >
            Flights ({acceptedFlights.length})
          </button>
          <button
            className={`convert-tab ${activeTab === 'recommendations' ? 'active' : ''}`}
            onClick={() => setActiveTab('recommendations')}
          >
            Recommendations ({interestedRecs.length})
          </button>
        </div>

        {/* Tab Content */}
        <div className="convert-tab-content">
          {/* Hotels Tab */}
          {activeTab === 'hotels' && (
            <div className="items-list">
              {acceptedHotels.length === 0 ? (
                <div className="empty-state">No accepted hotels in this proposal</div>
              ) : (
                acceptedHotels.map((hotel) => (
                  <div key={hotel.originalIndex} className="convert-item">
                    <label className="convert-item-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedItems.hotels.includes(hotel.originalIndex)}
                        onChange={() => handleCheckboxChange('hotels', hotel.originalIndex)}
                      />
                      <div className="convert-item-details">
                        <h4>{hotel.name}</h4>
                        <div className="item-meta">
                          <span>📍 {hotel.destination}</span>
                          {hotel.checkIn && hotel.checkOut && (
                            <span>📅 {new Date(hotel.checkIn).toLocaleDateString()} - {new Date(hotel.checkOut).toLocaleDateString()}</span>
                          )}
                          {hotel.roomType && <span>🛏️ {hotel.roomType}</span>}
                        </div>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Flights Tab */}
          {activeTab === 'flights' && (
            <div className="items-list">
              {acceptedFlights.length === 0 ? (
                <div className="empty-state">No accepted flights in this proposal</div>
              ) : (
                acceptedFlights.map((flight) => (
                  <div key={flight.originalIndex} className="convert-item">
                    <label className="convert-item-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedItems.flights.includes(flight.originalIndex)}
                        onChange={() => handleCheckboxChange('flights', flight.originalIndex)}
                      />
                      <div className="convert-item-details">
                        <h4>{flight.airline} {flight.flightNumber}</h4>
                        <div className="item-meta">
                          <span>✈️ {flight.from} → {flight.to}</span>
                          {flight.departureTime && (
                            <span>🕐 {new Date(flight.departureTime).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Recommendations Tab */}
          {activeTab === 'recommendations' && (
            <div className="items-list">
              {interestedRecs.length === 0 ? (
                <div className="empty-state">No interested recommendations in this proposal</div>
              ) : (
                interestedRecs.map((rec) => {
                  const getRecTitle = () => {
                    if (rec.recType === 'things-to-do') return rec.thingsToDoTitle;
                    if (rec.recType === 'restaurants') return rec.restaurantName;
                    if (rec.recType === 'activities') return rec.activityTitle;
                    if (rec.recType === 'events') return rec.eventTitle;
                    return 'Untitled';
                  };

                  const getRecIcon = () => {
                    if (rec.recType === 'things-to-do') return '🎯';
                    if (rec.recType === 'restaurants') return '🍽️';
                    if (rec.recType === 'activities') return '🎨';
                    if (rec.recType === 'events') return '🎉';
                    return '📍';
                  };

                  return (
                    <div key={rec.originalIndex} className="convert-item">
                      <label className="convert-item-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedItems.recommendations.includes(rec.originalIndex)}
                          onChange={() => handleCheckboxChange('recommendations', rec.originalIndex)}
                        />
                        <div className="convert-item-details">
                          <h4>{getRecTitle()}</h4>
                          <div className="item-meta">
                            <span>{getRecIcon()} {rec.recType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="modal-actions">
          <button className="modal-action-button" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="modal-action-button convert-button" 
            onClick={handleConvert}
          >
            Create Itinerary
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConvertToItineraryModal;

